import {
  Arrow,
  Content,
  Portal,
  Provider,
  Root,
  Trigger,
  type TooltipContentProps,
} from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Wrap the app once. Radix uses it to share the open/close delay so moving
 * between adjacent controls does not replay the full delay each time.
 */
export const TooltipProvider = Provider;

export interface TooltipProps {
  /** The tooltip text. A tooltip supplements a control that already has an
   *  accessible name — it is not a substitute for one, and it is unreachable
   *  on touch, so nothing essential belongs here. */
  content: ReactNode;
  children: ReactNode;
  side?: TooltipContentProps['side'];
  /**
   * Class applied to the portalled content. Radix renders it at document.body,
   * outside any `.pl-on-map` subtree — pass `pl-on-map` for a tooltip that
   * belongs to map chrome, or it will render as dark chrome over light.
   */
  portalClassName?: string | undefined;
}

export function Tooltip({ content, children, side = 'top', portalClassName }: TooltipProps) {
  return (
    <Root>
      <Trigger asChild>{children}</Trigger>
      <Portal>
        <Content
          side={side}
          sideOffset={6}
          className={cn(
            portalClassName,
            'z-(--pl-z-overlay) max-w-xs rounded-md border border-line bg-surface-raised',
            'px-2 py-1 text-xs text-fg shadow-overlay',
          )}
        >
          {content}
          <Arrow className="fill-surface-raised" />
        </Content>
      </Portal>
    </Root>
  );
}
